import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, IconButton, LinearProgress, Link, MenuItem, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TableSortLabel, TextField, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const t = (text) => window.__ ? window.__(text) : text;
const colorLabels = { red: 'Red', amber: 'Amber', green: 'Green' };

export default function TrafficLightTable({ initialList, endpoint, refreshKey, onEdit, onDelete }) {
    const [result, setResult] = useState(initialList);
    const [params, setParams] = useState(initialList.filters);
    const [search, setSearch] = useState(initialList.filters.q);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [retryKey, setRetryKey] = useState(0);
    const firstLoad = useRef(true);

    useEffect(() => {
        const timeout = setTimeout(() => setParams((current) => current.q === search.trim()
            ? current : { ...current, q: search.trim(), page: 1 }), 300);
        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        const query = new URLSearchParams(params).toString();
        const url = new URL(window.location.href);
        url.search = query;
        window.history.replaceState(null, '', url);
        if (firstLoad.current) {
            firstLoad.current = false;
            return undefined;
        }
        const controller = new AbortController();
        setLoading(true); setError('');
        fetch(`${endpoint}?${query}`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal })
            .then(async (response) => {
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || t('Unable to load traffic lights.'));
                if (controller.signal.aborted) return;
                setResult(data);
                if (data.current_page !== params.page) setParams((current) => ({ ...current, page: data.current_page }));
            })
            .catch((failure) => { if (!controller.signal.aborted) setError(failure.message); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [params, refreshKey, retryKey, endpoint]);

    const sort = (column) => setParams((current) => ({ ...current, page: 1, sort: column,
        direction: current.sort === column && current.direction === 'asc' ? 'desc' : 'asc' }));
    const detailsUrl = (light) => `${light.detail_url}?${new URLSearchParams(params)}`;
    return <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField size="small" label={t('Search traffic lights')} placeholder={t('Name, description, device or ID')}
                value={search} inputProps={{ maxLength: 255 }} onChange={(event) => setSearch(event.target.value)} sx={{ flex: 1 }} />
            <TextField select size="small" label={t('Device assignment')} value={params.assignment} sx={{ minWidth: 180 }}
                onChange={(event) => setParams({ ...params, assignment: event.target.value, page: 1 })}>
                <MenuItem value="all">{t('All traffic lights')}</MenuItem>
                <MenuItem value="assigned">{t('Assigned')}</MenuItem>
                <MenuItem value="unassigned">{t('Unassigned')}</MenuItem>
            </TextField>
            <Typography variant="body2" color="text.secondary">{result.total} {t('traffic lights')}</Typography>
        </Stack>
        {error && <Alert severity="error" action={<Button color="inherit" onClick={() => setRetryKey((key) => key + 1)}>{t('Retry')}</Button>}>{error}</Alert>}
        <Box aria-busy={loading}>
            <Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" aria-label={t('Traffic lights')} sx={{ minWidth: 700 }}>
                    <TableHead><TableRow>
                        {[['id', 'ID'], ['name', 'Name'], ['device', 'Device']].map(([column, label]) => <TableCell key={column} sortDirection={params.sort === column ? params.direction : false}>
                            <TableSortLabel active={params.sort === column} direction={params.sort === column ? params.direction : 'asc'} onClick={() => sort(column)}>{t(label)}</TableSortLabel>
                        </TableCell>)}
                        <TableCell>{t('Relays')}</TableCell><TableCell align="right">{t('Actions')}</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {result.data.map((light) => <TableRow key={light.id} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>#{light.id}</TableCell>
                            <TableCell sx={{ maxWidth: 360, py: 1.5 }}>
                                <Link href={detailsUrl(light)} underline="hover" fontWeight={600}>{light.name}</Link>
                                {light.description && <Typography variant="body2" color="text.secondary" noWrap title={light.description}>{light.description}</Typography>}
                            </TableCell>
                            <TableCell>{light.device_name || <Chip size="small" label={t('Unassigned')} />}</TableCell>
                            <TableCell><Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {light.relays?.length ? light.relays.map((relay) => <Chip key={relay.index} size="small" variant="outlined" label={`${t(colorLabels[relay.action] || relay.action)} #${relay.index}`} />) : '—'}
                            </Stack></TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Button size="small" component="a" href={detailsUrl(light)} endIcon={<ArrowForwardIcon />}>{t('Details')}</Button>
                                <IconButton size="small" aria-label={`${t('Edit')} ${light.name}`} onClick={() => onEdit(light)}><EditIcon fontSize="small" /></IconButton>
                                <IconButton size="small" aria-label={`${t('Delete')} ${light.name}`} onClick={() => onDelete(light)}><DeleteIcon fontSize="small" /></IconButton>
                            </TableCell>
                        </TableRow>)}
                        {!result.data.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                            {t(params.q || params.assignment !== 'all' ? 'No matching traffic lights.' : 'No traffic lights yet. Add your first traffic light.')}
                        </TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination component="div" count={result.total} page={params.page - 1} rowsPerPage={params.per_page}
                rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage={t('Rows per page:')}
                onPageChange={(_, page) => setParams({ ...params, page: page + 1 })}
                onRowsPerPageChange={(event) => setParams({ ...params, per_page: Number(event.target.value), page: 1 })} />
        </Box>
    </Stack>;
}
